/**
 * IdentityProvider — fetches `/api/me` on mount and exposes
 * `{ user, orgs, needsOnboarding, refreshIdentity }`.
 *
 * Sits above `AppContextProvider` in the tree. Routes that need a
 * workspace also need identity (you can't have a workspace without
 * being signed into an org), so identity loads first; AppContext
 * loads underneath only when a workspace is in scope.
 *
 * Three terminal states from `/api/me`:
 *   - 401          → user is null. App.tsx redirects to /auth/login.
 *   - 200, no orgs → user populated, needsOnboarding=true. App.tsx
 *                    redirects to /onboarding.
 *   - 200, orgs[] → user populated, needsOnboarding=false. Render
 *                   the requested route normally.
 *
 * `refreshIdentity()` is exposed for code paths that mutate the
 * identity surface (creating an org, accepting an invite, leaving
 * an org) — they call it after the mutation so the picker and
 * onboarding-redirect logic see the updated state without a page
 * reload.
 */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import { identityAPI, type IdentitySummary, type OrgWithRole } from '@/lib/api'
import { clearSuperadminMode } from '@/auth/superadminMode'

type IdentityState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'ready'; identity: IdentitySummary }
  | { status: 'error'; message: string }

interface IdentityContextValue {
  state: IdentityState
  /** Re-fetch `/api/me`. Call after any mutation that could change
   * the identity surface (createOrg, joinOrg, leaveOrg, deleteOrg). */
  refreshIdentity: () => Promise<void>
}

const IdentityContext = createContext<IdentityContextValue | null>(null)

export function IdentityProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<IdentityState>({ status: 'loading' })

  const refreshIdentity = useCallback(async () => {
    try {
      const result = await identityAPI.me()
      if (result === null) {
        setState({ status: 'unauthenticated' })
        return
      }
      setState({ status: 'ready', identity: result })
    } catch (err) {
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : String(err),
      })
    }
  }, [])

  useEffect(() => {
    void refreshIdentity()
  }, [refreshIdentity])

  // Defense in depth for the superadmin-mode toggle (#429 Wave 2 —
  // PR #521 Copilot review): explicit signOut clears it via
  // Header.handleSignOut, but a session can also end without going
  // through that path (cookie expiry, server-side termination,
  // account switch via a login page). Clear the toggle whenever
  // identity transitions OUT OF 'ready' so a stale `on` value can't
  // ride into the next session in the same tab. Tracks the previous
  // status across renders via a ref so we only fire on the
  // transition edge, not on every render.
  const prevStatus = useRef<typeof state.status>(state.status)
  useEffect(() => {
    if (prevStatus.current === 'ready' && state.status !== 'ready') {
      clearSuperadminMode()
    }
    prevStatus.current = state.status
  }, [state.status])

  return (
    <IdentityContext.Provider value={{ state, refreshIdentity }}>
      {children}
    </IdentityContext.Provider>
  )
}

/** Reads the identity context. Throws if the provider isn't mounted —
 * App.tsx guarantees it for every route below /auth/*. */
export function useIdentity(): IdentityContextValue {
  const ctx = useContext(IdentityContext)
  if (!ctx) {
    throw new Error('useIdentity called outside IdentityProvider — wrap your route in IdentityProvider via App.tsx.')
  }
  return ctx
}

/** Convenience accessor for the common "I just want the user/orgs,
 * I trust the route is inside the ready state" case. Throws when the
 * identity isn't ready — callers that need to handle loading/error
 * use `useIdentity()` directly and switch on `state.status`. */
export function useIdentityReady(): {
  user: IdentitySummary['user']
  orgs: OrgWithRole[]
  needsOnboarding: boolean
  refreshIdentity: () => Promise<void>
} {
  const { state, refreshIdentity } = useIdentity()
  if (state.status !== 'ready') {
    throw new Error(`useIdentityReady called in state '${state.status}' — wrap with a status check first.`)
  }
  return {
    user: state.identity.user,
    orgs: state.identity.orgs,
    needsOnboarding: state.identity.needsOnboarding,
    refreshIdentity,
  }
}
